import { Link } from "react-router-dom";
import { ArrowRight, Brush, Heart, Sparkles } from "lucide-react";
import { Kicker, Reveal, SectionHead } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";
import { MARQUEE_ITEMS } from "@/data/site";
import { useSiteData } from "@/context/SiteData";
import { usePageMeta } from "@/lib/meta";

const VALUES = [
  {
    icon: Heart,
    title: "Faith-Led Creativity",
    copy: "Every piece begins with God. Prayer and scripture shape the work before a brush ever touches canvas.",
  },
  {
    icon: Brush,
    title: "Excellence in Craft",
    copy: "Rich pigments, layered textures, and gold accents — each artwork is made slowly and made to last.",
  },
  {
    icon: Sparkles,
    title: "Art as Ministry",
    copy: "This is more than an art business. It is an artistic ministry devoted to encouraging and strengthening the saints.",
  },
];

export default function About() {
  usePageMeta(
    "About the Artist | Diamond's Divine Arts",
    "Meet Natalia, the artist behind Diamond's Divine Arts — a faith-inspired studio creating artwork that brings the Word of God to life through creativity."
  );
  const { t } = useSiteData();
  const bio = t("about_bio", "").split(/\n\s*\n/).filter((p) => p.trim());

  return (
    <>
      <section className="divine-rays relative overflow-hidden pb-20 pt-16 md:pb-28 md:pt-24" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker>About the Artist</Kicker>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#1C1815] sm:text-5xl lg:text-6xl">
              Natalia — the artist behind <span className="italic text-[#9A7A3F]">Diamond's Divine Arts</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <section className="py-20 md:py-32" data-testid="about-story-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="relative mx-auto max-w-sm">
                <div className="absolute -inset-3 rounded-t-[10rem] border border-[#D4AF37]/30" />
                <div className="relative aspect-[3/4] overflow-hidden rounded-t-[10rem] border border-[#D4AF37]/40 bg-[#F4EFE6]">
                  <img
                    src="/assets/natalya-portrait.webp"
                    alt="Natalya — artist and founder of Diamond's Divine Arts"
                    className="h-full w-full object-cover"
                  />
                </div>
                <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7E70]">
                  Natalia · Artist & Founder
                </p>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <SectionHead kicker="Her Story" title="Where Faith Meets the Canvas" />
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-6 text-base font-light leading-relaxed text-[#4A423A]">
                {bio.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </Reveal>

            <div className="mt-14 grid gap-8 sm:grid-cols-3">
              {VALUES.map((v, i) => (
                <Reveal key={v.title} delay={i * 0.1}>
                  <div className="border-t border-[#D4AF37]/40 pt-5" data-testid={`about-value-${i}`}>
                    <v.icon className="h-5 w-5 text-[#B89759]" />
                    <h3 className="mt-3 font-serif text-lg text-[#1C1815]">{v.title}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-[#4A423A]">{v.copy}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2}>
              <div className="mt-14 flex flex-col gap-4 sm:flex-row">
                <Link to="/mission" className="btn-primary" data-testid="about-mission-button">
                  Discover the Mission <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/booking" className="btn-outline" data-testid="about-book-button">
                  Book a Consultation
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand
        title="Create Something Sacred Together."
        copy="Commission a piece rooted in your story, your scripture, and your space."
      />
    </>
  );
}
